import { Box, Grid, Typography, Stack, Link, CircularProgress, Badge } from '@mui/material';

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from './Layout';
import {
  fetchUnreadCountByPatientIdAndFacilityId,
  subscribeToNewNotificationsByPatientId,
  subscribeToUpdatedNotificationsByPatientId
} from '../services/apiService';
import { fetchAuthSession } from 'aws-amplify/auth';
import { notMatchCustomErrorMessage } from '@/lib/sentryClient';
import * as Sentry from '@sentry/react';
import { executeGet } from '@/client/rest';
import { FacilityResponseType } from '@/features/reservation/types/FacilityResponseType';

export function NotificationFacilityList(props: any) {
  const [pause, setPause] = useState(false);
  const [facilityList, setFacilityList] = useState<any | null>(null);
  const [unreadCounts, setUnreadCounts] = useState<{ [key: string]: number }>({});
  const navigate = useNavigate();

  // 未読数の取得
  const fetchUnreadCounts = async (patientId: string) => {
    try {
      const counts = await fetchUnreadCountByPatientIdAndFacilityId(patientId);
      if (counts) {
        setUnreadCounts(counts);
      }
    } catch (error) {
      if (error instanceof Error && notMatchCustomErrorMessage(error.name)) {
        Sentry.captureException(error);
      }

      console.error('Error fetching unread counts:', error);
    }
  };

  // 患者IDの取得と未読数の更新
  const fetchPatientData = async () => {
    try {
      const session = await fetchAuthSession();
      const patientId = session?.userSub;
      if (patientId) await fetchUnreadCounts(patientId);
    } catch (error) {
      console.error('Error fetching patient data:', error);
    }
  };

  // 施設リストの取得
  const fetchFacilityList = async () => {
    try {
      const res = await executeGet<FacilityResponseType>({ apiName: 'medirecoWebApi', path: '/get-facility-list' });
      setFacilityList((res as FacilityResponseType)['facilities']);
    } catch (error) {
      if (error instanceof Error && notMatchCustomErrorMessage(error.name)) {
        Sentry.captureException(error);
      }

      console.error('Error fetching facility list:', error);
    }
  };

  // データの初期ロードと定期更新
  useEffect(() => {
    if (pause) return;

    fetchFacilityList();
    fetchPatientData();

    const timer = setInterval(() => {
      fetchFacilityList();
      fetchPatientData();
      setPause(true);
    }, 1000);

    return () => clearInterval(timer);
  }, [pause]);

  // サブスクリプションの設定
  useEffect(() => {
    const subscribeToNotifications = async () => {
      const session = await fetchAuthSession();
      const patientId = session.userSub;

      if (!patientId) return;

      // サブスクリプション設定（新規通知）
      const newNotificationSubscription = subscribeToNewNotificationsByPatientId(
        patientId,
        () => {
          fetchUnreadCounts(patientId); // 未読数を更新
        },
        (error) => console.error('新規通知サブスクリプションエラー:', error)
      );

      // サブスクリプション設定（更新通知）
      const updatedNotificationSubscription = subscribeToUpdatedNotificationsByPatientId(
        patientId,
        (data) => {
          if (data && data.onUpdateNotification) {
            fetchUnreadCounts(patientId); // 未読数を更新
          }
        },
        (error) => console.error('更新通知サブスクリプションエラー:', error)
      );

      // クリーンアップ関数でサブスクリプション解除
      return () => {
        newNotificationSubscription.unsubscribe();
        updatedNotificationSubscription.unsubscribe();
      };
    };

    subscribeToNotifications();
  }, []);

  return (
    <Layout title="施設一覧" backButton handleBack={() => navigate(-1)}>
      <Box sx={{ p: 2 }}>
        <Grid container direction={'row'} spacing={2} justifyContent={'center'}>
          <Grid item xs={12} md={6} sm={8}>
            <Grid container direction={'column'} spacing={2}>
              <Grid item xs={12} maxWidth={'100%'} mt={1}>
                <Stack direction={'column'}>
                  {facilityList ? (
                    Object.values(facilityList)?.map((data: any, i) => {
                      const unreadCount = unreadCounts[data.id] || 0; // 各施設の未読数を取得
                      return (
                        <Link
                          key={i}
                          onClick={() => {
                            navigate(`/notifications/facilities/${data.id}`);
                          }}
                          underline="none"
                          color="inherit"
                          mt={2}
                        >
                          <Grid
                            container
                            direction={'row'}
                            alignItems={'center'}
                            py={2}
                            borderRadius={1}
                            sx={{ backgroundColor: 'white' }}
                          >
                            <Typography className="font-bold" ml={1} mr={'auto'}>
                              {data.facility_name}
                            </Typography>
                            <Grid ml={'auto'} mr={1}>
                              {unreadCount > 0 && <Badge badgeContent={unreadCount} color="error" sx={{ mr: 1 }} />}
                            </Grid>
                          </Grid>
                        </Link>
                      );
                    })
                  ) : (
                    <Grid position={'absolute'} sx={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                      <CircularProgress size={'2rem'} color="inherit" />
                    </Grid>
                  )}
                </Stack>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Layout>
  );
}

export default NotificationFacilityList;
